/**
 * ScopeBar — the Customer -> Site switcher. Maps 1:1 to the tenancy spine
 * (Customer (org in the API) -> Site -> Device). UI copy says "Customer", never "Org". Selecting a scope updates the Zustand session store,
 * which re-keys every scope-prefixed query so the whole app follows the switch.
 *
 * IA note (anti-clone): this reinterprets the Meraki/Unleashed org/site switcher
 * as a two-step Radix dropdown pair, in Meridian's own visual language — no OEM
 * layout, color, or naming is copied.
 */
import { useMemo } from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { useLocation, useNavigate } from "react-router-dom";
import { useSession } from "@/stores/session";
import { isQuarantineOrg, orgLabel } from "@/lib/quarantine";
import { KNOWN_SECTIONS } from "./navItems";
import { SITE_SERVICES_TABS, activeSection, siteServicesTab } from "@/lib/navModel";
import { cn } from "@/lib/cn";

export function ScopeBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const me = useSession((s) => s.me);
  const currentOrg = useSession((s) => s.currentOrg());
  const currentSite = useSession((s) => s.currentSite());
  const currentOrgId = useSession((s) => s.currentOrgId);
  // Derive the site list with useMemo, NOT by selecting the store's
  // sitesForCurrentOrg() method: that returns a fresh .filter() array on every
  // render, so Zustand's snapshot comparison (Object.is) always sees a change
  // and re-renders forever -> "Maximum update depth exceeded" (React #185).
  // Memoizing on the raw inputs gives a stable reference between renders.
  const sites = useMemo(
    () => (me ? me.sites.filter((s) => s.org_id === currentOrgId) : []),
    [me, currentOrgId],
  );
  const setOrg = useSession((s) => s.setOrg);
  const setSite = useSession((s) => s.setSite);

  if (!me) return null;

  // A site inside the quarantine holding area is a placeholder, not a customer site.
  const quarantine = isQuarantineOrg(currentOrg);

  /** Switch org: update store AND the URL so the scope is deep-linkable. */
  function chooseOrg(orgId: string) {
    setOrg(orgId);
    navigate(`/o/${orgId}`);
  }

  /** Derive the active section (devices/clients/...) from the path so switching
   *  site keeps the operator on the same section. Falls back to devices. Switching TO a
   *  site from the customer Overview opens that site's Health (an Overview of one site
   *  would just redirect back to the customer level). */
  function currentSection(): string {
    // lib/navModel.activeSection: the same "which section is this?" rule the menu highlight
    // uses (the customer index /o/:org resolves to "overview").
    const found = activeSection(location.pathname);
    if (!found || !KNOWN_SECTIONS.includes(found)) return "devices";
    // A Site services tab (configure/vlans, configure/mesh) keeps its tab across a site switch.
    const tab = SITE_SERVICES_TABS.find((t) => t.id === siteServicesTab(location.pathname));
    return tab?.sub ? `${found}/${tab.sub}` : found;
  }

  /** Switch site: keep the current section (devices/clients/...) if we're in one. */
  function chooseSite(siteId: string | null) {
    setSite(siteId);
    if (!currentOrg) return;
    const section = currentSection();
    // Narrowing to a site from the Overview opens that site's Health instead.
    if (siteId) navigate(`/o/${currentOrg.id}/s/${siteId}/${section === "overview" ? "dashboard" : section}`);
    else navigate(`/o/${currentOrg.id}/${section}`);
  }

  // MSP operators (or anyone with more than one customer) get a way back to the
  // customers portal from any scope.
  const showCustomers = me.is_msp_admin || me.orgs.length > 1;

  return (
    <div className="flex items-center gap-2" aria-label="Scope selector">
      {showCustomers && (
        // Icon-only "back to all customers" — the adjacent picker is already labeled
        // "Customer", so a second "Customers" word here was redundant.
        <button
          type="button"
          onClick={() => navigate("/customers")}
          className={cn(
            "inline-flex min-h-touch items-center justify-center rounded border border-line bg-surface px-2.5 text-ink-muted",
            "hover:bg-surface-2",
          )}
          title="All customers"
          aria-label="All customers"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden className="text-ink-subtle">
            <path d="M10 4L6 8l4 4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      )}

      {/* Customer picker */}
      <DropdownMenu.Root>
        <DropdownMenu.Trigger
          className={cn(
            "inline-flex min-h-touch items-center gap-2 rounded border border-line bg-surface px-3 text-sm font-medium",
            "hover:bg-surface-2",
          )}
        >
          <span className="text-ink-subtle">Customer</span>
          {/* The quarantine holding area is labelled as such, never by its stored name
              ("Default Organization"), which read as a customer (2026-10-19). The label says
              "Quarantine", so the old separate pill would only repeat it. */}
          <span className="text-ink" data-testid="scope-customer">
            {orgLabel(currentOrg) ?? "Select customer"}
          </span>
          <Chevron />
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            align="start"
            sideOffset={6}
            className="z-toast min-w-56 rounded-lg border border-line bg-overlay p-1 shadow-sheet"
          >
            {me.orgs.map((org) => (
              <DropdownMenu.Item
                key={org.id}
                onSelect={() => chooseOrg(org.id)}
                className="flex min-h-touch cursor-pointer items-center justify-between rounded px-3 text-sm outline-none data-[highlighted]:bg-brand-soft"
              >
                <span>{orgLabel(org)}</span>
                {org.id === currentOrg?.id && <Check />}
              </DropdownMenu.Item>
            ))}
            {/* Create a new customer (org) — msp_admin only. Jumps to the customers portal with
                the create dialog auto-opened (Customers reads ?new=1). */}
            {me.is_msp_admin && (
              <>
                <DropdownMenu.Separator className="my-1 h-px bg-line" />
                <DropdownMenu.Item
                  onSelect={() => navigate("/customers?new=1")}
                  className="flex min-h-touch cursor-pointer items-center gap-2 rounded px-3 text-sm font-medium text-brand outline-none data-[highlighted]:bg-brand-soft"
                >
                  <span aria-hidden className="text-base leading-none">+</span>
                  <span>New customer…</span>
                </DropdownMenu.Item>
              </>
            )}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>

      <span aria-hidden className="text-ink-subtle">
        /
      </span>

      {/* Site picker — includes an "All sites" (org-wide) option. */}
      <DropdownMenu.Root>
        <DropdownMenu.Trigger
          className={cn(
            "inline-flex min-h-touch items-center gap-2 rounded border border-line bg-surface px-3 text-sm font-medium",
            "hover:bg-surface-2",
          )}
        >
          <span className="text-ink-subtle">Site</span>
          <span className="text-ink">
            {currentSite ? siteLabel(currentSite.name, quarantine) : "All sites"}
          </span>
          <Chevron />
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            align="start"
            sideOffset={6}
            className="z-toast min-w-56 rounded-lg border border-line bg-overlay p-1 shadow-sheet"
          >
            <DropdownMenu.Item
              onSelect={() => chooseSite(null)}
              className="flex min-h-touch cursor-pointer items-center justify-between rounded px-3 text-sm outline-none data-[highlighted]:bg-brand-soft"
            >
              <span>All sites</span>
              {!currentSite && <Check />}
            </DropdownMenu.Item>
            {sites.map((site) => (
              <DropdownMenu.Item
                key={site.id}
                onSelect={() => chooseSite(site.id)}
                className="flex min-h-touch cursor-pointer items-center justify-between rounded px-3 text-sm outline-none data-[highlighted]:bg-brand-soft"
              >
                <span>{siteLabel(site.name, quarantine)}</span>
                {site.id === currentSite?.id && <Check />}
              </DropdownMenu.Item>
            ))}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </div>
  );
}

/** Site name as shown in the picker; quarantine placeholders say what they are. */
function siteLabel(name: string, quarantine: boolean): string {
  return quarantine ? `${name} (placeholder)` : name;
}

/* --- Inline SVG glyphs (no icon-font/CDN; original marks, no OEM iconography) --- */
function Chevron() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden className="text-ink-subtle">
      <path d="M4 6l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function Check() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden className="text-brand">
      <path d="M3 8.5l3 3 7-7" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
